/**
 * AuthConsume component
 * Handles SSO token consumption from URL hash
 */
import { useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { consumeTokensFromUrl } from '@/global/auth/ssoUtils'

export function AuthConsume() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    const handleTokenConsumption = async () => {
      try {
        setLoading(true)
        const redirectTo = await consumeTokensFromUrl()
        
        // Wait a moment for the auth state to propagate
        setTimeout(() => {
          navigate(redirectTo, { replace: true })
        }, 1000)
      } catch (err) {
        console.error('Failed to consume auth tokens:', err)
        setError(err instanceof Error ? err.message : 'Authentication failed')
        
        // Redirect to login after a delay
        setTimeout(() => {
          navigate('/login', { replace: true })
        }, 3000)
      } finally {
        setLoading(false)
      }
    }

    handleTokenConsumption()
  }, [navigate])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Signing you in...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-destructive mb-4">
            <svg className="h-8 w-8 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold mb-2">Authentication Failed</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <p className="text-sm text-muted-foreground">Redirecting to login...</p>
        </div>
      </div>
    )
  }

  return null
}
